import { com } from '@eidu/form'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import EqualityHashMap from '../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import EntityRepository, { setGlobalAuthContext } from './EntityRepository'
import { logMessage } from '../util/Logging'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType

export type PaginatedEntitiesByType = ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>

export type UsePaginatedEntitiesOfTypeResult = {
  entitiesByTypeId: PaginatedEntitiesByType
  loadMore: () => void
  hasMore: boolean
  loading: boolean
}

type UsePaginatedEntitiesOfTypeParams = {
  typeIds: readonly EntityTypeId[]
  searchQuery?: string
  allTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  pageSize?: number
}

const DEFAULT_PAGE_SIZE = 10

const usePaginatedEntitiesOfType = ({
  typeIds,
  searchQuery,
  allTypes,
  pageSize = DEFAULT_PAGE_SIZE,
}: UsePaginatedEntitiesOfTypeParams): UsePaginatedEntitiesOfTypeResult => {
  const authContext = useContext<IAuthContext>(AuthContext)
  useEffect(() => {
    setGlobalAuthContext(authContext)
  }, [authContext])

  const nonNullTypes: ReadonlyMap<EntityTypeId, EntityType> = useMemo(
    () =>
      new EqualityHashMap(
        [...allTypes.entries()].flatMap(([k, v]) => (v ? ([[k, v]] as const) : [])) as [EntityTypeId, EntityType][]
      ),
    [JSON.stringify([...allTypes.keys()].map((it) => it.toString()))]
  )

  const repos = useMemo(
    () =>
      new EqualityHashMap(
        typeIds.map((typeId) => [typeId, new EntityRepository({ typeId, types: nonNullTypes, searchQuery, pageSize })])
      ),
    [
      JSON.stringify(typeIds.map((it) => it.toString())),
      JSON.stringify([...nonNullTypes.keys()].map((it) => it.toString())),
      searchQuery ?? '',
      pageSize,
    ]
  )

  useEffect(() => {
    logMessage(
      `usePaginatedEntitiesOfType repos ready: types=${typeIds.length}, pageSize=${pageSize}, searchQuery="${searchQuery ?? ''}"`
    )
  }, [repos])

  const [pageIndex, setPageIndex] = useState(0)
  const [entitiesByTypeId, setEntitiesByTypeId] = useState<PaginatedEntitiesByType>(new Map())
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const entitiesByTypeIdRef = useRef<PaginatedEntitiesByType>(entitiesByTypeId)
  useEffect(() => {
    entitiesByTypeIdRef.current = entitiesByTypeId
  }, [entitiesByTypeId])

  const lastSearchRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastSearchRef.current !== searchQuery) {
      logMessage(
        `Resetting pagination due to search change: prev="${lastSearchRef.current ?? ''}" -> next="${searchQuery ?? ''}"`
      )
      lastSearchRef.current = searchQuery
      setPageIndex(0)
      setEntitiesByTypeId(new Map())
      setHasMore(true)
    }
  }, [searchQuery, typeIds.length])

  const load = async (nextPage: number) => {
    if (loading) {
      logMessage(`load() ignored: already loading (nextPage=${nextPage})`)
      return
    }
    const start = Date.now()
    logMessage(
      `load() begin: nextPage=${nextPage}, query="${searchQuery ?? ''}", typeIds=${typeIds.length}, pageSize=${pageSize}`
    )
    setLoading(true)
    try {
      const entries: [EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>][] = await Promise.all(
        [...repos.entries()].map(async ([typeId, repo]) => {
          const page = await repo.getPage(nextPage)
          const filtered = new EqualityHashMap(
            [...page.entries()].flatMap(([id, entity]) => (entity ? ([[id, entity]] as const) : []))
          )
          const prev =
            (entitiesByTypeIdRef.current.get(typeId) as ReadonlyMap<EntityId, EntityWithLabelAndRelated>) ?? new Map()
          const combined = new EqualityHashMap([...prev.entries(), ...filtered.entries()])
          logMessage(
            `load() page fetched: typeId=${typeId.toString()}, page=${nextPage}, fetched=${filtered.size}, prev=${prev.size}, combined=${combined.size}`
          )
          return [typeId, combined]
        })
      )
      const merged: PaginatedEntitiesByType = new EqualityHashMap<
        EntityTypeId,
        ReadonlyMap<EntityId, EntityWithLabelAndRelated>
      >(entries)
      setEntitiesByTypeId(merged)

      const totals = await Promise.all([...repos.values()].map((repo) => repo.getNumItems()))
      const displayed = (nextPage + 1) * pageSize
      const totalAll = totals.reduce((acc, t) => acc + t, 0)
      const more = displayed < totalAll
      setHasMore(more)
      setPageIndex(nextPage)
      logMessage(
        `load() end: nextPage=${nextPage}, totals=[${totals.join(', ')}], totalAll=${totalAll}, displayed=${displayed}, hasMore=${more}, durationMs=${Date.now() - start}`
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setEntitiesByTypeId(new Map())
    setPageIndex(0)
    setHasMore(true)
    if (typeIds.length > 0) {
      load(0)
    }
  }, [repos])

  const loadMore = () => {
    if (hasMore && !loading) load(pageIndex + 1)
  }

  return { entitiesByTypeId, loadMore, hasMore, loading }
}

export default usePaginatedEntitiesOfType
